import { useState } from 'react'
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react'

interface TrocarSenhaModalProps {
  usuarioId: string
  usuarioNome: string
  onSuccess: () => void
  onCancel?: () => void
}

export default function TrocarSenhaModal({ usuarioId, usuarioNome, onSuccess, onCancel }: TrocarSenhaModalProps) {
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')

  const validarSenha = () => {
    if (novaSenha.length < 6) {
      setErro('A senha deve ter no mínimo 6 caracteres')
      return false
    }
    if (novaSenha !== confirmarSenha) {
      setErro('As senhas não coincidem')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')

    if (!validarSenha()) {
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/gestao/trocar-senha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usuario_id: usuarioId,
          nova_senha: novaSenha
        })
      })

      if (response.ok) {
        onSuccess()
      } else {
        const error = await response.json()
        setErro(error.error || 'Erro ao trocar senha')
      }
    } catch (error) {
      console.error('Erro ao trocar senha:', error)
      setErro('Erro ao processar requisição')
    } finally {
      setLoading(false)
    }
  }

  const getSenhaForca = () => {
    if (novaSenha.length === 0) return { texto: '', cor: '', largura: '0%' }
    if (novaSenha.length < 6) return { texto: 'Muito fraca', cor: 'bg-red-500', largura: '25%' }
    if (novaSenha.length < 8) return { texto: 'Fraca', cor: 'bg-orange-500', largura: '50%' }
    if (novaSenha.length < 12) return { texto: 'Boa', cor: 'bg-yellow-500', largura: '75%' }
    return { texto: 'Forte', cor: 'bg-green-500', largura: '100%' }
  }

  const forca = getSenhaForca()

  const isPrimeiroAcesso = !onCancel

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{isPrimeiroAcesso ? 'Primeiro Acesso' : 'Alterar Senha'}</h2>
              <p className="text-sm text-white/90">{isPrimeiroAcesso ? `Bem-vindo(a), ${usuarioNome}!` : usuarioNome}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {isPrimeiroAcesso && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Troca de senha obrigatória</p>
                  <p>Por segurança, você precisa definir uma nova senha antes de acessar o sistema.</p>
                </div>
              </div>
            </div>
          )}

          {erro && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{erro}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nova Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-12"
                  placeholder="Digite sua nova senha"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {mostrarSenha ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Indicador de força da senha */}
              {novaSenha.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Força da senha:</span>
                    <span className={`text-xs font-medium ${forca.cor.replace('bg-', 'text-')}`}>
                      {forca.texto}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${forca.cor}`}
                      style={{ width: forca.largura }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmar Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={mostrarConfirmar ? 'text' : 'password'}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-12"
                  placeholder="Confirme sua nova senha"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {mostrarConfirmar ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Indicador de senhas coincidentes */}
              {confirmarSenha.length > 0 && (
                <div className="mt-2 flex items-center space-x-2">
                  {novaSenha === confirmarSenha ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-green-600">As senhas coincidem</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <span className="text-xs text-red-600">As senhas não coincidem</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Requisitos */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-2">Requisitos da senha:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li className="flex items-center space-x-2">
                  <span className={novaSenha.length >= 6 ? 'text-green-600' : 'text-gray-400'}>•</span>
                  <span>Mínimo de 6 caracteres</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className={novaSenha === confirmarSenha && novaSenha.length > 0 ? 'text-green-600' : 'text-gray-400'}>•</span>
                  <span>Senhas devem coincidir</span>
                </li>
              </ul>
            </div>

            {/* Botões */}
            <div className="flex gap-3">
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                disabled={loading || novaSenha.length < 6 || novaSenha !== confirmarSenha}
                className={`${onCancel ? 'flex-1' : 'w-full'} bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 relative overflow-hidden group ${
                loading || novaSenha.length < 6 || novaSenha !== confirmarSenha
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:shadow-lg hover:shadow-orange-500/25 hover:-translate-y-0.5'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <span className="relative z-10 flex items-center justify-center">
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Alterando senha...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Confirmar Nova Senha
                  </>
                )}
              </span>
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {isPrimeiroAcesso 
              ? 'Esta senha será usada para seus próximos acessos ao sistema'
              : 'Sua nova senha entrará em vigor imediatamente'
            }
          </p>
        </div>
      </div>
    </div>
  )
}
